import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import {
    Alert,
    Badge,
    Box,
    Button,
    Card, CardContent,
    Checkbox,
    Chip, CircularProgress,
    Collapse,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    List, ListItem,
    Paper,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';

function ChefPanelNew() {
    const [orders, setOrders] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending,preparing');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [newOrdersCount, setNewOrdersCount] = useState(0);
    const [checklists, setChecklists] = useState({});
    const [expandedOrders, setExpandedOrders] = useState({});

    // Автообновление каждые 5 секунд
    useEffect(() => {
        fetchOrders();
        fetchStatistics();

        const interval = setInterval(() => {
            fetchOrders(true); // Тихое обновление без loader
            fetchStatistics();
        }, 5000);

        return () => clearInterval(interval);
    }, [filter]);

    const fetchOrders = async (silent = false) => {
        try {
            if (!silent) setLoading(true);

            const response = await axios.get(`/api/chef/orders?status=${filter}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.success) {
                const newOrders = response.data.data;

                // Подсчитываем новые заказы
                if (orders.length > 0 && silent) {
                    const oldOrderIds = new Set(
                        orders.flatMap(e => e.orders.map(o => o.order_id))
                    );
                    const newOrderIds = newOrders.flatMap(e => e.orders.map(o => o.order_id));
                    const freshOrders = newOrderIds.filter(id => !oldOrderIds.has(id));

                    if (freshOrders.length > 0) {
                        setNewOrdersCount(prev => prev + freshOrders.length);
                        // Звуковое уведомление
                        playNotificationSound();
                    }
                }

                setOrders(newOrders);

                // Загружаем чеклисты для всех заказов
                newOrders.forEach(event => {
                    event.orders.forEach(order => {
                        fetchChecklist(order.order_id);
                    });
                });
            }
            setError('');
        } catch (err) {
            if (!silent) {
                setError(err.response?.data?.error || 'Ошибка загрузки заказов');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const fetchChecklist = async (orderId) => {
        try {
            const response = await axios.get(`/api/chef/orders/${orderId}/checklist`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.success) {
                setChecklists(prev => ({
                    ...prev,
                    [orderId]: response.data.data
                }));
            }
        } catch (err) {
            console.error('Ошибка загрузки чеклиста:', err);
        }
    };

    const playNotificationSound = () => {
        try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIF2m98OScTgwOUKXh8bllHQU2jdXwzn4vBSV6yPDajj0JFF+47OuoVRQLR6Hh8r5tIAQqfs/y14k2Bxdqv/DlnFAMD1Gl4fG4Zh0FNY3V8M9+LwUme8jw2Y89CRRfuOzrqFQTC0eh4fK+bCAEKn7P8teJNgcXar/w5ZxPDA9RpeHxuGYdBTWN1fDPfi8FJnvI8NmPPQkUX7js66hUEwtHoeHyvm4gBCp+z/LXiTYHF2m88OWcTwwPUKXh8bhkHAU2jdXwzn4vBSV6yPDZjz0JFF+47OuoVRQLR6Hh8r5tIAQqfs/y14k2BxdqvPDlnE8MD1Cl4fG5ZRwFNo3V8M5+LwUme8jw2o89CRRfuOzrqFUSC0eh4fK+bR8EKn7P8tiJNgcXar/w5ZxPDA9RpeHxuGYdBTWN1fDPfi8FJnvI8NqPPQkUX7js66hUEwtHoeHyvm0fBCp+z/LYiTYHF2q/8OWcTwwPUaXh8bhlHQU1jdXwz34vBSZ7yPDajz0JFF+47OuoVBMLR6Hh8r5sIAQqfs/y2Ik2Bxdqv/DlnE8MD1Gl4fG4ZR0FNY3V8M9+LwUme8jw2o89CRRfuOzrqFQTC0eh4fK+bCAEKn7P8tiJNgcXar/w5ZxPDA9RpeHxuGUdBTWN1fDPfi8FJnvI8NqPPQkUX7js66hUEwtHoeHyvm0gBCp+z/LXiTYHF2m88OWcTwwPUKXh8bhlHQU1jdXwz34vBSZ7yPDajz0JFF+47OuoVRQLR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2o89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NqPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDajz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2o89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPAkUYLjs66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjzwJFGC47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y88CRRguOzrqFUSC0eh4fK+bCAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hUEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OScTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9RpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUaXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2BxdpvPDlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47OuoVRILR6Hh8r5tIAQqfs/y14k2Bxdpv/DlnE8MD1Cl4fG4ZhwFNY3V8M5+LwUle8jw2Y89CRRfuOzrqFUSC0eh4fK+bSAEKn7P8teJNgcXab/w5ZxPDA9QpeHxuGYcBTWN1fDOfi8FJXvI8NmPPQkUX7js66hVEgtHoeHyvm0gBCp+z/LXiTYHF2m/8OWcTwwPUKXh8bhmHAU1jdXwzn4vBSV7yPDZjz0JFF+47Ouo=');
            audio.volume = 0.3;
            audio.play().catch(() => { }); // Игнорируем ошибки autoplay
        } catch (e) { }
    };

    const fetchStatistics = async () => {
        try {
            const response = await axios.get('/api/chef/dashboard', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.success) {
                setStatistics(response.data.data);
            }
        } catch (err) {
            console.error('Ошибка загрузки статистики:', err);
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            const response = await axios.put(`/api/chef/orders/${orderId}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );

            if (response.data.success) {
                setSuccess(`Статус изменен на "${getStatusLabel(newStatus)}"`);
                setTimeout(() => setSuccess(''), 3000);
                fetchOrders();
                fetchStatistics();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Ошибка изменения статуса');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleChecklistToggle = async (orderId, itemId, currentValue) => {
        try {
            const response = await axios.put(`/api/chef/orders/${orderId}/checklist/${itemId}`,
                { is_checked: !currentValue },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );

            if (response.data.success) {
                // Обновляем чеклист локально
                setChecklists(prev => ({
                    ...prev,
                    [orderId]: prev[orderId].map(item =>
                        item.id === itemId ? { ...item, is_checked: !currentValue } : item
                    )
                }));
            }
        } catch (err) {
            console.error('Ошибка обновления чеклиста:', err);
        }
    };

    const toggleOrderExpand = (orderId) => {
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'preparing': return 'info';
            case 'ready': return 'success';
            case 'served': return 'default';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending': return 'В очереди';
            case 'preparing': return 'Готовится';
            case 'ready': return 'Готово';
            case 'served': return 'Подано';
            case 'cancelled': return 'Отменен';
            default: return status;
        }
    };

    const formatEventDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = date - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        // Форматируем дату и время слота
        const dateStr = date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
        });

        let urgencyText = '';
        if (hours < 0) urgencyText = '⏰ Прошло';
        else if (hours === 0 && minutes < 30) urgencyText = `⚠️ СРОЧНО! Через ${minutes} мин`;
        else if (hours === 0) urgencyText = `🔥 Скоро! Через ${minutes} мин`;
        else if (hours < 2) urgencyText = `⏱️ Через ${hours}ч ${minutes}м`;
        else if (hours < 24) urgencyText = `📅 Сегодня через ${hours}ч`;

        return { dateStr, timeStr, urgencyText, isPastDue: hours < 0, isUrgent: hours === 0 && minutes < 30 };
    };

    const getChecklistProgress = (orderId) => {
        const checklist = checklists[orderId] || [];
        if (checklist.length === 0) return 0;
        const checked = checklist.filter(item => item.is_checked).length;
        return (checked / checklist.length) * 100;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header с бейджем новых заказов */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RestaurantIcon sx={{ mr: 1, fontSize: 40 }} />
                    <Box>
                        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                            Кухня
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Автообновление каждые 5 секунд
                        </Typography>
                    </Box>
                </Box>

                {newOrdersCount > 0 && (
                    <Badge badgeContent={newOrdersCount} color="error" max={99}>
                        <NotificationsActiveIcon sx={{ fontSize: 40, color: 'error.main' }} />
                    </Badge>
                )}
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Крупная статистика - КВАДРАТНЫЕ карты */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={6} md={3}>
                        <Card sx={{
                            height: { xs: 180, md: 220 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            aspectRatio: '1',
                            background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)'
                        }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <HourglassEmptyIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
                                <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                    {statistics.pending_orders}
                                </Typography>
                                <Typography variant="h6" color="text.secondary">
                                    В очереди
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{
                            height: { xs: 180, md: 220 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            aspectRatio: '1',
                            background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)'
                        }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <RestaurantIcon sx={{ fontSize: 60, color: 'info.main', mb: 2 }} />
                                <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                                    {statistics.preparing_orders}
                                </Typography>
                                <Typography variant="h6" color="text.secondary">
                                    В работе
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{
                            height: { xs: 180, md: 220 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            aspectRatio: '1',
                            background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)'
                        }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                                <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                    {statistics.ready_orders}
                                </Typography>
                                <Typography variant="h6" color="text.secondary">
                                    Готово
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card sx={{
                            height: { xs: 180, md: 220 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            aspectRatio: '1',
                            background: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)'
                        }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <DeliveryDiningIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                                <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    {Math.round(statistics.average_prep_time || 0)}
                                </Typography>
                                <Typography variant="h6" color="text.secondary">
                                    Минут (ср.)
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Крупные кнопки фильтров */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                        <Button
                            variant={filter === 'pending,preparing' ? 'contained' : 'outlined'}
                            onClick={() => setFilter('pending,preparing')}
                            fullWidth
                            size="large"
                            sx={{ height: 60, fontSize: '1.1rem' }}
                        >
                            Активные
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Button
                            variant={filter === 'pending' ? 'contained' : 'outlined'}
                            onClick={() => setFilter('pending')}
                            fullWidth
                            size="large"
                            sx={{ height: 60, fontSize: '1.1rem' }}
                        >
                            В очереди
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Button
                            variant={filter === 'preparing' ? 'contained' : 'outlined'}
                            onClick={() => setFilter('preparing')}
                            fullWidth
                            size="large"
                            sx={{ height: 60, fontSize: '1.1rem' }}
                        >
                            Готовятся
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Button
                            variant={filter === 'ready' ? 'contained' : 'outlined'}
                            onClick={() => setFilter('ready')}
                            fullWidth
                            size="large"
                            sx={{ height: 60, fontSize: '1.1rem' }}
                        >
                            Готовые
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Заказы - КРУПНЫЕ квадратные карты */}
            {orders.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                        Заказов нет
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Все заказы обработаны или отсутствуют
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {orders.map((event) => (
                        <Grid item xs={12} key={event.event_id}>
                            <Paper sx={{
                                p: 4,
                                backgroundColor: formatEventDate(event.event_date).isUrgent ? '#fff3e0' : 'white',
                                border: formatEventDate(event.event_date).isUrgent ? '3px solid #ff9800' : 'none'
                            }}>
                                {/* Заголовок события */}
                                <Box sx={{ mb: 3, borderBottom: 2, borderColor: 'divider', pb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {event.event_title}
                                        </Typography>
                                        {formatEventDate(event.event_date).urgencyText && (
                                            <Chip
                                                label={formatEventDate(event.event_date).urgencyText}
                                                color={formatEventDate(event.event_date).isUrgent ? 'error' :
                                                    formatEventDate(event.event_date).isPastDue ? 'default' : 'warning'}
                                                size="large"
                                                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                            🕐 СЛОТ: {formatEventDate(event.event_date).timeStr}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            📅 {formatEventDate(event.event_date).dateStr}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Заказы */}
                                <Grid container spacing={3}>
                                    {event.orders.map((order) => {
                                        const checklist = checklists[order.order_id] || [];
                                        const progress = getChecklistProgress(order.order_id);
                                        const isExpanded = expandedOrders[order.order_id];

                                        return (
                                            <Grid item xs={12} sm={6} lg={4} key={order.order_id}>
                                                <Card sx={{
                                                    height: '100%',
                                                    border: 3,
                                                    borderColor: order.status === 'pending' ? 'warning.main' :
                                                        order.status === 'preparing' ? 'info.main' :
                                                            'divider',
                                                    minHeight: 400
                                                }}>
                                                    <CardContent sx={{ p: 3 }}>
                                                        {/* ВРЕМЯ СЛОТА - ОЧЕНЬ КРУПНО И ЗАМЕТНО */}
                                                        <Box sx={{
                                                            mb: 3,
                                                            p: 3,
                                                            backgroundColor: formatEventDate(event.event_date).isUrgent ? '#ff5722' : '#2196f3',
                                                            color: 'white',
                                                            borderRadius: 3,
                                                            textAlign: 'center',
                                                            boxShadow: 3
                                                        }}>
                                                            <Typography variant="overline" sx={{ display: 'block', mb: 1, fontSize: '0.9rem', letterSpacing: 2 }}>
                                                                ⏰ ГОТОВИМ К СЛОТУ ⏰
                                                            </Typography>
                                                            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                                                                🕐 {formatEventDate(event.event_date).timeStr}
                                                            </Typography>
                                                            {formatEventDate(event.event_date).urgencyText && (
                                                                <Chip
                                                                    label={formatEventDate(event.event_date).urgencyText}
                                                                    sx={{
                                                                        backgroundColor: 'rgba(255,255,255,0.3)',
                                                                        color: 'white',
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.95rem'
                                                                    }}
                                                                    size="medium"
                                                                />
                                                            )}
                                                        </Box>

                                                        {/* Header заказа */}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                            <Chip
                                                                label={getStatusLabel(order.status)}
                                                                color={getStatusColor(order.status)}
                                                                size="large"
                                                                sx={{ fontSize: '1rem', fontWeight: 'bold' }}
                                                            />
                                                            {order.item_type === 'bundle' && (
                                                                <Chip label="Набор" size="large" color="secondary" />
                                                            )}
                                                        </Box>

                                                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                                            {order.item_name}
                                                        </Typography>

                                                        {order.item_description && (
                                                            <Typography variant="body2" color="text.secondary" paragraph>
                                                                {order.item_description}
                                                            </Typography>
                                                        )}

                                                        <Typography variant="h6" sx={{ mb: 2 }}>
                                                            <strong>Количество:</strong> {order.quantity}
                                                        </Typography>

                                                        {order.order_notes && (
                                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                                📝 {order.order_notes}
                                                            </Alert>
                                                        )}

                                                        {/* Прогресс чеклиста */}
                                                        {checklist.length > 0 && (
                                                            <Box sx={{ mb: 2 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                    <PlaylistAddCheckIcon sx={{ mr: 1 }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                        Прогресс: {Math.round(progress)}%
                                                                    </Typography>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => toggleOrderExpand(order.order_id)}
                                                                        sx={{ ml: 'auto' }}
                                                                    >
                                                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                    </IconButton>
                                                                </Box>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={progress}
                                                                    sx={{ height: 8, borderRadius: 4 }}
                                                                />
                                                            </Box>
                                                        )}

                                                        {/* Чеклист */}
                                                        <Collapse in={isExpanded}>
                                                            <List dense sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
                                                                {checklist.map((item) => (
                                                                    <ListItem key={item.id} disablePadding>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={item.is_checked}
                                                                                    onChange={() => handleChecklistToggle(
                                                                                        order.order_id,
                                                                                        item.id,
                                                                                        item.is_checked
                                                                                    )}
                                                                                    color={item.is_critical ? 'error' : 'primary'}
                                                                                />
                                                                            }
                                                                            label={
                                                                                <Typography
                                                                                    sx={{
                                                                                        textDecoration: item.is_checked ? 'line-through' : 'none',
                                                                                        fontWeight: item.is_critical ? 'bold' : 'normal'
                                                                                    }}
                                                                                >
                                                                                    {item.item_name}
                                                                                    {item.category && (
                                                                                        <Chip
                                                                                            label={item.category}
                                                                                            size="small"
                                                                                            sx={{ ml: 1, height: 20 }}
                                                                                        />
                                                                                    )}
                                                                                </Typography>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Collapse>

                                                        {/* Кнопки действий - КРУПНЫЕ */}
                                                        <Box sx={{ mt: 3 }}>
                                                            {order.status === 'pending' && (
                                                                <Button
                                                                    fullWidth
                                                                    variant="contained"
                                                                    color="info"
                                                                    size="large"
                                                                    startIcon={<HourglassEmptyIcon />}
                                                                    onClick={() => handleStatusChange(order.order_id, 'preparing')}
                                                                    sx={{ height: 60, fontSize: '1.1rem', fontWeight: 'bold' }}
                                                                >
                                                                    Начать готовить
                                                                </Button>
                                                            )}
                                                            {order.status === 'preparing' && (
                                                                <Button
                                                                    fullWidth
                                                                    variant="contained"
                                                                    color="success"
                                                                    size="large"
                                                                    startIcon={<CheckCircleIcon />}
                                                                    onClick={() => handleStatusChange(order.order_id, 'ready')}
                                                                    sx={{ height: 60, fontSize: '1.1rem', fontWeight: 'bold' }}
                                                                >
                                                                    Готово
                                                                </Button>
                                                            )}
                                                            {order.status === 'ready' && (
                                                                <Button
                                                                    fullWidth
                                                                    variant="contained"
                                                                    size="large"
                                                                    startIcon={<DeliveryDiningIcon />}
                                                                    onClick={() => handleStatusChange(order.order_id, 'served')}
                                                                    sx={{ height: 60, fontSize: '1.1rem', fontWeight: 'bold' }}
                                                                >
                                                                    Подано
                                                                </Button>
                                                            )}
                                                        </Box>

                                                        {order.preparation_time && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                                                                ⏱ Приготовлено за {order.preparation_time} мин
                                                            </Typography>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
}

export default ChefPanelNew;
